import React, { useState, useEffect, createContext, useContext } from 'react';
import { Globe, ChevronDown, MapPin, Check, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context to share selected municipality across admin pages
const AdminTenantContext = createContext(null);

export const useAdminTenant = () => {
  const context = useContext(AdminTenantContext);
  if (!context) {
    return { selectedMunicipio: null, setSelectedMunicipio: () => {}, municipios: [], isGlobal: true };
  }
  return context;
};

export const AdminTenantProvider = ({ children, isAdminMaster, userMunicipioId }) => {
  // Lazy initializer: read localStorage SYNCHRONOUSLY so the very first render
  // (and any side-effects depending on selectedMunicipio) already has the
  // tenant context. Without this, there was a race where the writer effect
  // (further down) wrote 'global' before any reader effect could hydrate the
  // saved value, causing pages like /admin/configuracoes to load the GLOBAL
  // doc on first mount even when the admin had previously selected a tenant.
  const [selectedMunicipio, setSelectedMunicipio] = useState(() => {
    if (!isAdminMaster) return null;
    try {
      const saved = localStorage.getItem('admin_selected_municipio');
      if (saved && saved !== 'null' && saved !== 'global') {
        return JSON.parse(saved);
      }
    } catch { /* ignore */ }
    return null;
  });
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMunicipios = async () => {
      if (!isAdminMaster) {
        // Non-global admin: force their assigned municipality
        if (userMunicipioId) {
          try {
            const response = await axios.get(`${API_URL}/api/municipios?ativo=false`);
            const allMunicipios = response.data || [];
            const userMun = allMunicipios.find(m => m.id === userMunicipioId);
            if (userMun) {
              setSelectedMunicipio(userMun);
            }
          } catch (error) {
            console.error('Error fetching municipality:', error);
          }
        }
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/municipios?ativo=false`);
        setMunicipios(response.data || []);
      } catch (error) {
        console.error('Error fetching municipalities:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMunicipios();
  }, [isAdminMaster, userMunicipioId]);

  // Save selection to localStorage
  useEffect(() => {
    if (selectedMunicipio) {
      localStorage.setItem('admin_selected_municipio', JSON.stringify(selectedMunicipio));
    } else if (isAdminMaster) {
      localStorage.setItem('admin_selected_municipio', 'global');
    }
  }, [selectedMunicipio, isAdminMaster]);

  const value = {
    selectedMunicipio,
    setSelectedMunicipio: isAdminMaster ? setSelectedMunicipio : () => {}, // Non-global admins can't switch
    municipios,
    loading,
    isGlobal: isAdminMaster && !selectedMunicipio,
    municipioId: selectedMunicipio?.id || null,
    municipioNome: selectedMunicipio?.nome || (isAdminMaster ? 'Todos os Municípios' : 'Meu Município')
  };

  return (
    <AdminTenantContext.Provider value={value}>
      {children}
    </AdminTenantContext.Provider>
  );
};

export default function AdminMunicipioSelector({ isAdminMaster, userMunicipio }) {
  const { selectedMunicipio, setSelectedMunicipio, municipios, loading } = useAdminTenant();

  if (!isAdminMaster) {
    // Non-admin master users see their fixed municipality
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg">
        <MapPin className="w-4 h-4" />
        <span className="text-sm font-medium truncate max-w-[120px]">
          {userMunicipio?.nome || 'Meu Município'}
        </span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg">
        <Globe className="w-4 h-4 animate-pulse" />
        <span className="text-sm">Carregando...</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg h-auto"
          data-testid="admin-municipio-selector"
        >
          {selectedMunicipio ? (
            <>
              <MapPin className="w-4 h-4 text-[#FFA52A]" />
              <span className="text-sm font-medium truncate max-w-[120px]">
                {selectedMunicipio.nome}
              </span>
            </>
          ) : (
            <>
              <Globe className="w-4 h-4 text-[#FFA52A]" />
              <span className="text-sm font-medium">Todos os Municípios</span>
            </>
          )}
          <ChevronDown className="w-4 h-4 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Contexto do Município
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Global option */}
        <DropdownMenuItem
          onClick={() => setSelectedMunicipio(null)}
          className="flex items-center justify-between"
          data-testid="select-global"
        >
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-600" />
            <span>Todos os Municípios</span>
          </div>
          {!selectedMunicipio && <Check className="w-4 h-4 text-green-600" />}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Municipality list */}
        <div className="max-h-[300px] overflow-y-auto">
          {municipios.map((mun) => (
            <DropdownMenuItem
              key={mun.id}
              onClick={() => setSelectedMunicipio(mun)}
              className="flex items-center justify-between"
              data-testid={`select-municipio-${mun.slug}`}
            >
              <div className="flex items-center gap-2">
                <MapPin className={`w-4 h-4 ${mun.ativo ? 'text-green-600' : 'text-gray-400'}`} />
                <div className="flex flex-col">
                  <span className={!mun.ativo ? 'text-gray-400' : ''}>
                    {mun.nome}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {mun.uf} • /{mun.slug}
                  </span>
                </div>
              </div>
              {selectedMunicipio?.id === mun.id && <Check className="w-4 h-4 text-green-600" />}
            </DropdownMenuItem>
          ))}
        </div>
        
        {municipios.length === 0 && (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Nenhum município cadastrado
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
